import React from 'react';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { showDateNoTime, showTime } from '@libs/NwMoment';
import { isFullDay } from '@libs/NwMoment';

import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const TimeDisplayContainer = styled.div`
    text-transform: uppercase;
    padding: 0.25em 0.5em 0.25em 0;
    white-space: nowrap;
    min-width: 8.5rem;
    text-align: right;
    opacity: ${(props) => props.isAvailable && 0.5};

    @media only screen and (max-width: 720px) {
        text-align: left;
        width: 100%;
        padding: 0.25em 0.5em;
        border-bottom: ${(props) => props.theme.DefaultBorder};
        font-weight: bold;
    }

    .start-end-time {
        display: flex;
        flex-direction: column;

        @media only screen and (max-width: 720px) {
            flex-direction: row;
            .start-time {
                margin-right: 10px;
            }
        }
    }

    span {
        display: block;
        font-size: 1rem;

        &.start-time {
            font-weight: bold;

            @media only screen and (max-width: 720px) {
                font-weight: bold;
            }
        }
        &.all-day {
            font-weight: normal;
            font-size: ${(props) => props.theme.FontSizeSm};

            @media only screen and (max-width: 720px) {
                font-weight: bold;
                font-size: 1rem;
            }
        }
        &.reminder {
            font-weight: bold;
            color: ${(props) => props.theme.DangerColor};
            font-size: 0.6rem;

            @media only screen and (max-width: 720px) {
                font-weight: bold;
                font-size: 1rem;
            }
        }
    }

    small {
        display: block;
        font-weight: normal;
        font-size: ${(props) => props.theme.FontSizeSm};

        em {
            display: block;
            font-style: normal;
        }
    }

    div.end-date {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        svg {
            margin-right: 1em;
        }
    }

    div.started-date {
        display: block;
    }

    .call-time {
        padding-top: 0.5rem;
        font-size: ${(props) => props.theme.FontSizeSm};
        font-weight: normal;
        text-transform: uppercase;
        line-height: 1.1em;
    }
`;

const TimeWithBreaks = ({ st, et, sb, eb }) => {
    return (
        <div className="double-time-wrapper">
            <div className="start-end-time">
                <span className="start-time">{showTime(st)}</span>
                <div className="end-date">
                    <span>
                        <NwIcon icon={byPrefixAndName.fasl['arrow-to-right']} size="xs" />
                    </span>
                    <span className="end-time">{showTime(sb)}</span>
                </div>
            </div>
            <div className="start-end-time">
                <span className="start-time">{showTime(eb)}</span>
                <div className="end-date">
                    <span>
                        <NwIcon icon={byPrefixAndName.fasl['arrow-to-right']} size="xs" />
                    </span>
                    <span className="end-time">{showTime(et)}</span>
                </div>
            </div>
        </div>
    );
};

const SimpleTime = ({ st, et }) => {
    return (
        <div className="start-end-time">
            <span className="start-time">{showTime(st)}</span>
            <div className="end-date">
                <span>
                    <NwIcon icon={byPrefixAndName.fasl['arrow-to-right']} size="xs" />
                </span>
                <span className="end-time">{showTime(et)}</span>
            </div>
        </div>
    );
};

const OnlyStartTime = ({ st }) => {
    return <span className="start-time">{showTime(st)}</span>;
};

const AllDayTime = () => {
    return <span className="all-day">ALL DAY</span>;
};

const NoTimes = ({ st, et }) => {
    return (
        <>
            <div className="start-date">
                <span className="end-time">{showDateNoTime(st)}</span>
            </div>
            <div className="end-date">
                <span>
                    <NwIcon icon={byPrefixAndName.fasl['arrow-to-right']} size="xs" />
                </span>
                <span className="end-time">{showDateNoTime(et)}</span>
            </div>
        </>
    );
};

const PeriodTime = ({ st, et }) => {
    return (
        <>
            <div className="started-date">
                <small>
                    <strong>STARTED ON</strong>
                </small>
                {isFullDay(st, et) ? (
                    <small>{showDateNoTime(st)}</small>
                ) : (
                    <small>
                        {showDateNoTime(st)} • {showTime(st)}
                    </small>
                )}
            </div>
            <div className="started-date">
                <small>
                    <strong>WILL END ON</strong>
                </small>
                {isFullDay(st, et) ? (
                    <small>{showDateNoTime(et)}</small>
                ) : (
                    <small>
                        {showDateNoTime(et)} • {showTime(et)}
                    </small>
                )}
            </div>
        </>
    );
};

const PeriodEnd = ({ st, et }) => {
    return (
        <div className="start-end-time">
            <span className="start-time">
                <NwIcon icon={byPrefixAndName.fasl['arrow-to-right']} />{' '}
                {isFullDay(st, et) ? showDateNoTime(et) : showTime(et)}
            </span>
            <div className="started-date">
                <small>STARTED ON</small>
                {isFullDay(st, et) ? (
                    <small>{showDateNoTime(st)}</small>
                ) : (
                    <small>
                        {showDateNoTime(st)} • {showTime(st)}
                    </small>
                )}
            </div>
        </div>
    );
};

const PeriodStart = ({ st, et }) => {
    return (
        <div className="start-end-time">
            <span className="start-time">{isFullDay(st, et) ? showDateNoTime(st) : showTime(st)}</span>
            <div className="end-date">
                <small>
                    <NwIcon icon={byPrefixAndName.fasl['arrow-to-right']} />
                </small>
                <div>
                    <small>{showDateNoTime(et)}</small>
                    {!isFullDay(st, et) && <small>{showTime(et)}</small>}
                </div>
            </div>
        </div>
    );
};

const EventTimeDisplay = ({ event, eventType, isAvailable = false, currentDate = null }) => {
    const st_string = event.FromDate;
    const et_string = event.ToDate;
    const sb_string = event.StartBreak;
    const eb_string = event.EndBreak;
    const st = getMoment(st_string);
    const et = getMoment(et_string);
    const sb = sb_string ? getMoment(sb_string) : null;
    const eb = eb_string ? getMoment(eb_string) : null;
    const call_time = event.CallTime;
    const moddates = event.ModelsDates;

    let dontShowCallTime = false;

    const showBookingEventTime = (st, et, sb, eb) => {
        if (sb && eb) {
            return <TimeWithBreaks st={st} et={et} sb={sb} eb={eb} />;
        }
        if (st.hours() === 0 && st.minutes() === 0 && et.hours() === 23 && et.minutes() === 59) {
            return <AllDayTime />;
        } else {
            if (st.hours() === et.hours() && st.minutes() === et.minutes()) {
                return <OnlyStartTime st={st} />;
            } else {
                return <SimpleTime st={st} et={et} />;
            }
        }
    };

    const writeTime = () => {
        if (!eventType) {
            // Fallback for when eventType is not provided
            if (!et || st.isSame(et, 'minute')) {
                return <OnlyStartTime st={st} />;
            }
            if (et && st.isSame(et, 'day')) {
                if (st.hours() === 0 && st.minutes() === 0 && et.hours() === 23 && et.minutes() === 59) {
                    return <AllDayTime />;
                }
                return <SimpleTime st={st} et={et} />;
            } else {
                return <NoTimes st={st} et={et} />;
            }
        }

        switch (eventType.code.toLowerCase()) {
            case 'accommodation':
                if (!st.isSame(et, 'day')) {
                    return <NoTimes st={st} et={et} />;
                }
                return <AllDayTime />;
            case 'casting':
            case 'job':
            case 'ext':
                let timetoshow = '';
                if (moddates && moddates.length) {
                    if (moddates.length > 1) {
                        dontShowCallTime = true;
                        return <span className="all-day">{moddates.length} time frames</span>;
                    } else {
                        const stmd = getMoment(moddates[0].FromDate);
                        const etmd = getMoment(moddates[0].ToDate);
                        const sbmd = moddates[0].StartBreak ? getMoment(moddates[0].StartBreak) : null;
                        const ebmd = moddates[0].EndBreak ? getMoment(moddates[0].EndBreak) : null;
                        timetoshow = showBookingEventTime(stmd, etmd, sbmd, ebmd);
                    }
                } else {
                    timetoshow = showBookingEventTime(st, et, sb, eb);
                }
                return timetoshow;
            default:
                if (!et || st.isSame(et, 'minute')) {
                    //single date (no endtime)
                    return <OnlyStartTime st={st} />;
                }
                if (et && st.isSame(et, 'day')) {
                    if (st.hours() === 0 && st.minutes() === 0 && et.hours() === 23 && et.minutes() === 59) {
                        return <AllDayTime />;
                    }
                    return <SimpleTime st={st} et={et} />;
                } else {
                    //longer than one day
                    if (currentDate) {
                        const date = getMoment(currentDate);
                        if (!st.isSame(date, 'days')) {
                            //it's not start date
                            if (!et.isSame(date, 'days')) {
                                //between start and end
                                return <PeriodTime st={st} et={et} />;
                            } else {
                                //it's end date
                                return <PeriodEnd st={st} et={et} />;
                            }
                        } else {
                            //it's start date
                            return <PeriodStart st={st} et={et} />;
                        }
                    } else {
                        // No current date provided, show simple date range
                        return <NoTimes st={st} et={et} />;
                    }
                }
        }
    };

    return (
        <TimeDisplayContainer isAvailable={isAvailable}>
            {writeTime()}
            {call_time && !dontShowCallTime && (
                <span className="call-time">
                    Call Time
                    <br />
                    {call_time}
                </span>
            )}
        </TimeDisplayContainer>
    );
};

export default EventTimeDisplay;
